import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import { colors } from '../../tailwind.config';

export const MyPreset = definePreset(Aura, {
  primitive: colors,
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
    overlay: {
      modal: {
        borderRadius: '0.8rem',
      },
      popover: {
        borderRadius: '10px',
      },
    },
    colorScheme: {
      light: {
        surface: {
          0: '#FAFAFA',
          50: '#EBEBEC',
          100: '#DDDDDE',
          200: '#C1C1C3',
          300: '#A5A5A8',
          400: '#89898D',
          500: '#6D6D72',
          600: '#515157',
          700: '#35353C',
          800: '#292930',
          900: '#1E1E25',
          950: '#17171B',
        },
      },
    },
  },
  components: {
    steps: {
      colorScheme: {
        light: {
          itemNumber: {
            background: '{surface.100}',
            activeBackground: '{blue.200}',
          },
        },
        dark: {
          itemNumber: {
            background: '{surface.900}',
            activeBackground: '{blue.900}',
          },
        },
      },
    },
    avatar: {
      root: {
        background: 'transparent',
      },
    },
    drawer: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
          },
        },
        dark: {
          root: {
            background: '{teal.900}',
          },
        },
      },
    },
    button: {
      root: {
        label: {
          fontWeight: '400',
        },
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{blue.600}',
              hoverBackground: '{blue.800}',
            },
          },
          text: {
            secondary: {
              hoverBackground: '{blue.100}',
            },
          },
        },
        dark: {
          text: {
            secondary: {
              hoverBackground: '{teal.800}',
            },
          },
        },
      },
    },
    chip: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.50}',
          },
        },
        dark: {
          root: {
            background: '{teal.800}',
          },
        },
      },
    },
    togglebutton: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.50}',
          },
        },
      },
    },
    datatable: {
      sortIcon: {
        size: '14px',
      },
      colorScheme: {
        light: {
          header: {
            background: 'none',
            color: '{teal.900}',
          },
          row: {
            background: '{surface.0}',
            hoverBackground: 'color-mix(in srgb, {blue.100} 60%, transparent)',
            selectedBackground: '{primary.100}',
          },
        },
        dark: {
          header: {
            background: 'none',
            color: '{surface.0}',
          },
          row: {
            background: '{teal.900}',
            hoverBackground: 'color-mix(in srgb, {blue.900} 40%, transparent)',
            selectedBackground: '{primary.900}',
          },
        },
      },
    },
    inputtext: {
      colorScheme: {
        light: {
          root: {
            disabledColor: '{surface.500}',
          },
        },
        dark: {
          root: {
            background: '{teal.950}',
          },
        },
      },
    },
    textarea: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
          },
        },
        dark: {
          root: {
            background: '{teal.950}',
          },
        },
      },
    },
    multiselect: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            disabledBackground: '{surface.50}',
          },
          overlay: {
            background: '{surface.0}',
          },
          option: {
            focusBackground: '{surface.50}',
            selectedBackground: '{surface.50}',
            selectedFocusBackground: '{surface.50}',
          },
        },
        dark: {
          root: {
            background: '{teal.950}',
            disabledBackground: '{teal.950}',
          },
          overlay: {
            background: '{teal.900}',
          },
          option: {
            focusBackground: '{teal.950}',
            selectedBackground: '{teal.900}',
            selectedFocusBackground: '{teal.900}',
          },
        },
      },
    },
    checkbox: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
          },
        },
        dark: {
          root: {
            background: '{teal.950}',
          },
        },
      },
    },
    select: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            disabledBackground: '{surface.50}',
          },
          overlay: {
            background: '{surface.0}',
          },
        },
        dark: {
          root: {
            background: '{teal.950}',
            disabledBackground: '{teal.950}',
          },
          overlay: {
            background: '{teal.900}',
          },
          option: {
            focusBackground: '{blue.400}',
          },
        },
      },
    },
    datepicker: {
      colorScheme: {
        light: {
          dropdown: {
            hoverBackground: '{blue.100}',
          },
          date: {
            hoverBackground: '{blue.100}',
          },
          selectMonth: {
            hoverBackground: '{blue.100}',
          },
          selectYear: {
            hoverBackground: '{blue.100}',
          },
        },
        dark: {
          panel: {
            background: '{teal.900}',
          },
          header: {
            background: '{teal.900}',
          },
          dropdown: {
            background: '{teal.950}',
            hoverBackground: '{teal.800}',
          },
          date: {
            hoverBackground: '{teal.800}',
          },
          selectMonth: {
            hoverBackground: '{teal.800}',
          },
          selectYear: {
            hoverBackground: '{teal.800}',
          },
        },
      },
    },
    virtualscroller: {
      colorScheme: {
        dark: {
          loader: {
            mask: {
              background: '{teal.900}',
            },
          },
        },
      },
    },
    dialog: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
          },
        },
        dark: {
          root: {
            background: '{teal.900}',
          },
        },
      },
    },
    toolbar: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
          },
        },
      },
    },
    treetable: {
      colorScheme: {
        light: {
          headerCell: {
            background: '{surface.50}',
          },
        },
        dark: {
          headerCell: {
            background: '{teal.950}',
          },
          row: {
            background: '{teal.900}',
          },
        },
      },
    },
    accordion: {
      colorScheme: {
        light: {
          header: {
            background: '{surface.0}',
            hoverBackground: '{surface.50}',
            activeBackground: '{surface.0}',
          },
          content: {
            background: '{surface.0}',
          },
        },
        dark: {
          header: {
            background: '{teal.900}',
            hoverBackground: '{teal.900}',
            activeBackground: '{teal.900}',
            activeHoverBackground: '{teal.900}',
          },
          content: {
            background: '{teal.900}',
          },
        },
      },
    },
    panel: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: '{surface.100}',
          },
        },
        dark: {
          root: {
            background: '{teal.900}',
            borderColor: '{teal.500}',
          },
        },
      },
    },
    menu: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: '{surface.100}',
          },
        },
        dark: {
          root: {
            background: '{teal.900}',
            borderColor: '{teal.700}',
          },
        },
      },
    },
  },
});
